import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { ShoppingCart, Shield, Clock, Users, BookOpen, Eye, Loader2, ExternalLink } from 'lucide-react';
import PaymentResultDialog from './PaymentResultDialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { trackViewContent, trackInitiateCheckout, trackAddToCart, trackAddPaymentInfo, PRODUCT_DATA } from '@/lib/metaPixel';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import heroNotesImage1 from '@/assets/hero-notes.jpeg';
import heroNotesImage2 from '@/assets/hero-notes-2.jpeg';

declare global {
  interface Window {
    Cashfree: any;
  }
}

const notesPreviewImages = [
  { src: heroNotesImage1, alt: "Class 10 Science Short Notes - Magnetic Effect of Electric Current" },
  { src: heroNotesImage2, alt: "Class 10 Science Short Notes - Chemical Reactions and Electricity" },
];

const HeroSection: React.FC = () => {
  const { user, profile, hasPaymentAccess } = useSupabaseAuth();
  const navigate = useNavigate();
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  
  const autoplayPlugin = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideCount, setSlideCount] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    setSlideCount(carouselApi.scrollSnapList().length);
    setCurrentSlide(carouselApi.selectedScrollSnap());
    carouselApi.on("select", () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const scrollToSlide = useCallback((index: number) => {
    carouselApi?.scrollTo(index);
  }, [carouselApi]);

  // Track ViewContent on mount
  useEffect(() => {
    try {
      trackViewContent({
        content_name: PRODUCT_DATA.content_name,
        content_category: 'Study Notes',
        content_ids: PRODUCT_DATA.content_ids,
        content_type: PRODUCT_DATA.content_type,
        value: PRODUCT_DATA.value,
        currency: PRODUCT_DATA.currency,
      });
    } catch (e) {
      // Ignore tracking errors
    }
  }, []);

  const isLoggedIn = !!user;
  const hasPurchased = isLoggedIn && hasPaymentAccess;
  
  console.log('[HeroSection] Render - user:', !!user, 'isLoggedIn:', isLoggedIn, 'hasPurchased:', hasPurchased);

  // Handle buy now - redirect to signup page if not logged in
  const handleBuyNow = () => {
    console.log('[HeroSection] handleBuyNow called, isLoggedIn:', isLoggedIn);
    
    if (!isLoggedIn) {
      // Redirect to signup checkout page
      navigate('/signup-checkout');
    } else {
      // Already logged in - track and start payment
      try { trackAddToCart(); trackInitiateCheckout(); } catch (e) {}
      setIsProcessing(true);
      startPayment();
    }
  };

  const startPayment = () => {
    if (!window.Cashfree) {
      toast({ title: "Loading...", description: "Please wait" });
      setTimeout(() => startPayment(), 500);
      return;
    }
    processPayment();
  };

  const processPayment = async () => {
    try {
      const orderId = `order_${Date.now()}`;
      
      const { data, error } = await supabase.functions.invoke('create-cashfree-order', {
        body: {
          orderId,
          orderAmount: 149,
          customerName: profile?.full_name || 'Customer',
          customerEmail: profile?.email || user?.email || 'customer@example.com',
          customerPhone: profile?.mobile || '9999999999',
        },
      });

      if (error || !data?.success) {
        throw new Error(data?.error || error?.message || 'Failed to create order');
      }

      // Store payment record in background (non-blocking)
      if (user) {
        supabase.from('payments').insert({
          user_id: user.id,
          order_id: orderId,
          amount: 149,
          status: 'pending',
        });
      }

      // Track AddPaymentInfo before redirecting to payment gateway
      try { trackAddPaymentInfo(); } catch (e) {}

      // Open Cashfree checkout immediately - always use _self for fastest redirect
      const cashfree = window.Cashfree({ mode: 'production' });
      cashfree.checkout({
        paymentSessionId: data.paymentSessionId,
        redirectTarget: '_self',
      });

    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Payment failed';
      toast({ title: "Error", description: msg, variant: "destructive" });
      setIsProcessing(false);
    }
  };

  const handleNavigateToDashboard = () => {
    setShowResultDialog(false);
    navigate('/dashboard');
  };

  const handleRetryPayment = () => {
    setShowResultDialog(false);
    startPayment();
  };

  return (
    <>
      <section 
        id="hero"
        aria-labelledby="hero-heading"
        className="relative overflow-hidden bg-gradient-subtle py-12 md:py-16"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent" aria-hidden="true" />
        
        <div className="container relative px-4 md:px-6">
          <div className="mx-auto max-w-4xl">
            {/* Notes Preview Carousel */}
            <figure className="group relative mb-8">
              <Carousel 
                className="w-full" 
                opts={{ loop: true }}
                plugins={[autoplayPlugin.current]}
                setApi={setCarouselApi}
              >
                <CarouselContent>
                  {notesPreviewImages.map((image, index) => (
                    <CarouselItem key={index}>
                      <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card shadow-card transition-all duration-300 hover:shadow-lg">
                        <div className="w-full overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100">
                          <img 
                            src={image.src} 
                            alt={image.alt}
                            className="w-full h-auto max-h-[70vh] object-contain mx-auto"
                            loading={index === 0 ? "eager" : "lazy"}
                          />
                        </div>
                        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-primary/10 blur-2xl transition-all duration-500 group-hover:bg-primary/20" aria-hidden="true" />
                        <div className="absolute -bottom-4 -left-4 h-32 w-32 rounded-full bg-blue-500/10 blur-2xl transition-all duration-500 group-hover:bg-blue-500/20" aria-hidden="true" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-2 md:-left-12" />
                <CarouselNext className="right-2 md:-right-12" />
              </Carousel>
              
              {/* Dot Indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: slideCount }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollToSlide(index)}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'w-8 bg-primary' 
                        : 'w-2.5 bg-primary/30 hover:bg-primary/50'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </figure>

            {/* Title */}
            <h1 id="hero-heading" className="text-center text-2xl font-bold leading-tight tracking-tight text-foreground md:text-3xl lg:text-4xl animate-fade-up">
              SCIENCE NOTES CLASS 10TH 2025-2026
              <span className="mt-2 block text-gradient">
                (TOPPERS SHORT NOTES)
              </span>
            </h1>

            {/* Pricing */}
            <div className="mt-8 text-center animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-4">
                🎉 Limited Time Offer
              </div>
              
              <div className="flex items-center justify-center gap-4">
                <span className="text-2xl font-medium text-muted-foreground line-through decoration-2">
                  ₹499
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-primary md:text-5xl">
                    ₹149
                  </span>
                  <span className="text-lg text-muted-foreground">/only</span>
                </div>
              </div>
              
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-green-500/10 px-4 py-2 text-sm font-medium text-green-600">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Save 70% Today
              </div>
            </div>

            {/* Buy Now / View Notes Button */}
            <div className="mt-8 text-center animate-fade-up relative z-50" style={{ animationDelay: '0.3s' }}>
              {hasPurchased ? (
                <>
                  <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-success/10 px-4 py-2 text-success">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">Thank you for purchasing!</span>
                  </div>
                  <Button 
                    variant="hero" 
                    size="xl" 
                    className="w-full max-w-md gap-3 text-lg pointer-events-auto"
                    onClick={() => navigate('/dashboard')}
                  >
                    <BookOpen className="h-5 w-5" />
                    View Your Notes
                  </Button>
                </>
              ) : isLoggedIn ? (
                <Button 
                  variant="hero" 
                  size="xl" 
                  className="w-full max-w-md gap-3 text-lg"
                  onClick={handleBuyNow}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-5 w-5" />
                      BUY NOW - ₹149
                    </>
                  )}
                </Button>
              ) : (
                <Link to="/signup-checkout" className="w-full max-w-md inline-block">
                  <Button 
                    variant="hero" 
                    size="xl" 
                    className="w-full gap-3 text-lg"
                  >
                    <ShoppingCart className="h-5 w-5" />
                    BUY NOW - ₹149
                  </Button>
                </Link>
              )}

              {/* Trust Badges */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  <span>Secure Payment</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>Instant Access</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span>1,300+ Happy Students</span>
                </div>
              </div>

              {/* Demo Notes Preview Button - Opens in new tab for reliable viewing */}
              <div className="mt-8">
                <a 
                  href="https://drive.google.com/file/d/1RJLSGJS7wvuitr9m5LBYgkP3FgiMdQom/view?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="gap-2 border-primary/50 text-primary hover:bg-primary/10"
                  >
                    <Eye className="h-5 w-5" />
                    Preview Demo Notes - Free
                    <ExternalLink className="h-4 w-4 ml-1" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PaymentResultDialog
        open={showResultDialog}
        onOpenChange={setShowResultDialog}
        success={paymentSuccess}
        onNavigateToDashboard={handleNavigateToDashboard}
        onRetry={handleRetryPayment}
      />
    </>
  );
};

export default HeroSection;
